import { HakiliMascot, MascotBadge, type MascotState } from "@hakili/ui";
import { t } from "@hakili/i18n";
import type { ReactNode } from "react";

import { stageChipWords } from "./stage-words.js";

/**
 * The parts the two analyst lists (« Pipeline », « Dossiers ») and the chef's « Pipeline de l'équipe » share, drawn as D2 draws them
 * (frames 01 to 05, 66): the dark initials tile, the dot chip, the pill tabs, the card that holds a table, and the card that says
 * why a list is empty or could not be read (« le veilleur » above the sentence).
 */

/** D2's `dotchip`: an 8 px dot and the words, in one of the theme's tone pairs. */
export const DOT_CHIP =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[5px] text-[length:var(--hk-fs-meta)] font-semibold leading-none";
export const DOT_TONES = {
  neutral: "bg-[color:var(--hk-neutral-bg)] text-[color:var(--hk-text)]",
  brand: "bg-[color:var(--hk-brand-tint)] text-[color:var(--hk-brand-strong)]",
  ok: "bg-[color:var(--hk-ok-bg)] text-[color:var(--hk-ok)]",
  bad: "bg-[color:var(--hk-bad-bg)] text-[color:var(--hk-bad)]",
} as const;
/** The dot is a 14 px tall box with the circle inside, so the chip's text and its dot sit on one measured line. */
const DOT = "h-3.5 w-2 shrink-0 bg-[radial-gradient(circle_at_center,currentColor_0_4px,transparent_4.5px)]";

const toneOfStage = (stage: string, mode: "open" | "all"): keyof typeof DOT_TONES => {
  if (stage === "decaisse") return "ok";
  if (stage === "refuse") return "bad";
  if (stage === "perdu") return "neutral";
  if (stage === "retour" && mode === "open") return "neutral";
  return "brand";
};

/** The stage chip of a list row, in D2's one word per stage and its tones (`open`: « Chez Hakili » is neutral; `all`: it stays brand). */
export function StageDotChip(props: {
  readonly stage: string;
  readonly mode: "open" | "all";
}): React.JSX.Element {
  return (
    <span
      className={`${DOT_CHIP} ${DOT_TONES[toneOfStage(props.stage, props.mode)]}`}
      data-testid="bank-statut"
      data-kind="chip-label"
    >
      <i className={DOT} aria-hidden="true" />
      {stageChipWords(props.stage)}
    </span>
  );
}

/** D2's `.who` cell: a 36 px dark tile with the initials, the name, and the reference line under it. */
export function FileWho(props: {
  readonly initials: string;
  readonly name: string;
  readonly line: string;
  readonly testId: string;
  readonly lineTestId: string;
}): React.JSX.Element {
  return (
    <span className="flex items-center gap-3" data-testid={props.testId}>
      <span
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[color:var(--hk-text)] text-[length:var(--hk-fs-body)] font-semibold text-white"
      >
        {props.initials}
      </span>
      <span className="min-w-0" data-testid={props.lineTestId}>
        <b className="block font-semibold">{props.name}</b>
        <small
          className="block whitespace-nowrap text-[length:var(--hk-fs-meta)] text-[color:var(--hk-text-2)]"
          data-meta
        >
          {props.line}
        </small>
      </span>
    </span>
  );
}

/** The card a list's table sits in (D2 `.card`): hairline, large radius, the table runs edge to edge. */
export function ListCard(props: {
  readonly children: ReactNode;
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <div
      className="overflow-hidden rounded-[var(--hk-r-lg)] border border-[color:var(--hk-line)] bg-[color:var(--hk-surface)]"
      data-testid={props.testId}
    >
      {props.children}
    </div>
  );
}

/** D2's `.tabs1`: pill tabs, the count in the mono face, the selected one dark. */
export function PillTabs(props: {
  readonly label: string;
  readonly tabs: readonly { readonly id: string; readonly label: string; readonly count: number }[];
  readonly value: string;
  readonly showCounts?: boolean;
  readonly onChange: (id: string) => void;
  readonly testIdOf: (id: string) => string;
}): React.JSX.Element {
  return (
    <div className="mb-3.5 flex gap-1.5 overflow-x-auto pb-0.5" role="tablist" aria-label={props.label}>
      {props.tabs.map((tab) => {
        const selected = tab.id === props.value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => {
              props.onChange(tab.id);
            }}
            data-testid={props.testIdOf(tab.id)}
            className={
              "inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-[9px] text-[length:var(--hk-fs-body)] font-semibold transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[color:var(--hk-focus)] " +
              (selected
                ? "border-[color:var(--hk-text)] bg-[color:var(--hk-text)] text-white"
                : "border-[color:var(--hk-line)] bg-white text-[color:var(--hk-text-2)] hover:bg-[color:var(--hk-surface-2)]")
            }
          >
            {tab.label}
            {props.showCounts === false ? null : (
              <em
                className={
                  "font-[family-name:var(--hk-mono)] text-[length:var(--hk-fs-meta)] font-semibold not-italic " +
                  (selected ? "text-white/80" : "text-[color:var(--hk-text-2)]")
                }
              >
                {tab.count}
              </em>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** « le veilleur » at D2's `.mc` size, with its corner badge where the state has one. */
export function Veilleur(props: {
  readonly state: MascotState;
  readonly size: "page" | "tiny";
  readonly className?: string;
}): React.JSX.Element {
  return (
    <span
      className={`relative block shrink-0 ${props.size === "page" ? "w-[150px]" : "w-12"} ${props.className ?? ""}`}
      data-testid="bank-veilleur"
      data-state={props.state}
    >
      <HakiliMascot state={props.state} label={t("bank.bay.mascotte")} />
      {props.state === "idle" || props.state === "done" ? null : (
        <span className="absolute -bottom-1 -right-1 block h-7 w-7">
          <MascotBadge state={props.state} />
        </span>
      )}
    </span>
  );
}

/** D2's `.empty` and `.errorp`: the mascot, a heading, a sentence and (for an error) the retry, in a card. */
export function ListState(props: {
  readonly kind: "empty" | "error";
  readonly title: string;
  readonly sentence: string;
  readonly action?: ReactNode;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <ListCard>
      <div
        className="grid justify-items-center gap-2.5 px-6 py-12 text-center"
        data-testid={props.testId}
        role={props.kind === "error" ? "alert" : undefined}
      >
        <Veilleur state={props.kind === "error" ? "failed" : "idle"} size="page" />
        <h2 className="text-[length:var(--hk-fs-h3)] font-semibold">{props.title}</h2>
        <p className="max-w-[52ch] text-[color:var(--hk-text-2)]">{props.sentence}</p>
        {props.action}
      </div>
    </ListCard>
  );
}
